import React from 'react';
import T from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { Button, Container } from '@mui/material';
import { Dialog } from 'web-components';
import { initialStaticValues, metricDataType } from '../../../../../attrs/sensorConfig';
import { StaticSensorDataSchema } from '../../../../../attrs/formValidation';
import { getFormikHelperText } from '../../../../../helpers/utils';
import {
  StyledDialogActions,
  StyledDialogBody,
  StyledDialogHeader,
  StyledDialogSubHeader
} from '../../../../../attrs/style';
import { COLOR_PRIMARY } from '../../../../../attrs/colors';
import { StaticValuesFormEditFields } from './StaticValuesFormEditFields';

const StaticValuesDialogEdit = ({ onSubmit, open, setOpen, sensor, getError, namespace }) => {
  const { t } = useTranslation();

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Formik
      initialValues={initialStaticValues}
      onSubmit={(values, { resetForm }) => {
        onSubmit(values);
        resetForm({ values: '' });
      }}
      validationSchema={StaticSensorDataSchema}
      enableReinitialize
    >
      {({ dirty, errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, setValues, values }) => {
        const getHelperText = getFormikHelperText({ errors, t });

        return (
          <Dialog open={open}>
            <>
              <StyledDialogHeader highlightColor={COLOR_PRIMARY} onClose={() => setOpen(false)}>
                {t('machines.form.sensor.modal.edit')}
              </StyledDialogHeader>
              <StyledDialogSubHeader>{t('machines.form.sensor.modal.subtitle')}</StyledDialogSubHeader>
              <StyledDialogBody>
                <Container style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <StaticValuesFormEditFields
                    getError={getError}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    setFieldValue={setFieldValue}
                    setValues={setValues}
                    getHelperText={getHelperText}
                    values={values}
                    sensor={sensor}
                    namespace={namespace}
                  />
                </Container>
              </StyledDialogBody>
              <StyledDialogActions>
                <Button variant="text" color="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  data-testid="machine-details--add-static-sensor-button"
                  disabled={isSubmitting || Object.keys(errors).length !== 0 || !dirty}
                  onClick={handleSubmit}
                  variant="contained"
                  type="button"
                >
                  {t('form.update')}
                </Button>
              </StyledDialogActions>
            </>
          </Dialog>
        );
      }}
    </Formik>
  );
};

StaticValuesDialogEdit.propTypes = {
  getError: T.func.isRequired,
  namespace: T.string.isRequired,
  onSubmit: T.func.isRequired,
  open: T.bool.isRequired,
  setOpen: T.func.isRequired,
  sensor: T.instanceOf(Object).isRequired,
  values: T.shape({
    id: T.string,
    commission_number: T.string,
    configuration: T.shape({
      plc: T.shape({
        metrics: T.arrayOf(metricDataType.isRequired).isRequired
      })
    })
  }).isRequired
};

export { StaticValuesDialogEdit };
